import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Textarea from '../../components/Textarea';
import Select from '../../components/Select';
import { apiService } from '../../services/apiService';
import { EQUIPMENT_TYPES } from '../../utils/constants';
import { useToast } from '../../contexts/ToastContext';

const AddOtherEquipment = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    type: '',
    clientId: '',
    siteId: '',
    serialNumber: '',
    lastMaintenanceDate: '',
    nextMaintenanceDate: '',
    expirationDate: '',
    location: '',
    physicalCondition: '',
    notes: '',
  });

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    const clientId = searchParams.get('clientId');
    const siteId = searchParams.get('siteId');
    if (clientId || siteId) {
      setFormData((prev) => ({
        ...prev,
        ...(clientId ? { clientId: String(clientId) } : {}),
        ...(siteId ? { siteId: String(siteId) } : {}),
      }));
    }
  }, [searchParams]);

  useEffect(() => {
    if (formData.clientId) {
      loadSites(formData.clientId);
    } else {
      setSites([]);
      setFormData(prev => ({ ...prev, siteId: '' }));
    }
  }, [formData.clientId]);

  useEffect(() => {
    if (formData.lastMaintenanceDate) {
      const date = new Date(formData.lastMaintenanceDate);
      date.setMonth(date.getMonth() + 6);
      const nextDate = date.toISOString().split('T')[0];
      setFormData(prev => ({ ...prev, nextMaintenanceDate: nextDate }));
    }
  }, [formData.lastMaintenanceDate]);

  const loadClients = async () => {
    try {
      const data = await apiService.getClients();
      setClients(data);
    } catch (error) {
      console.error('Failed to load clients:', error);
    }
  };

  const loadSites = async (clientId) => {
    try {
      const data = await apiService.getSitesByClient(clientId);
      setSites(data);
    } catch (error) {
      console.error('Failed to load sites:', error);
      setSites([]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.type) newErrors.type = "Le type d'équipement est requis";
    if (!formData.clientId) newErrors.clientId = 'Le client est requis';
    if (!formData.siteId) newErrors.siteId = 'Le site est requis';
    if (!formData.serialNumber) newErrors.serialNumber = 'Le numéro de série est requis';
    if (!formData.lastMaintenanceDate) newErrors.lastMaintenanceDate = 'La date de vérification est requise';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);
      await apiService.createEquipment(formData);
      navigate('/equipment');
    } catch (error) {
      console.error('Failed to create equipment:', error);
      const errorMessage = error.message || "Erreur lors de la création de l'équipement";
      setErrors({ submit: errorMessage });
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const clientOptions = clients.map((client) => ({
    value: client.id,
    label: `${client.name} - ${client.company}`,
  }));

  const siteOptions = sites.map((site) => ({
    value: site.id,
    label: site.name,
  }));

  const equipmentTypeOptions = [
    { value: EQUIPMENT_TYPES.RIA, label: 'RIA (Robinet Incendie Armé)' },
    { value: EQUIPMENT_TYPES.BDI, label: 'BDI (Bouche Incendie)' },
    { value: EQUIPMENT_TYPES.CABINET, label: 'Armoire Incendie' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Button
          variant="outline"
          icon={ArrowLeft}
          onClick={() => navigate('/equipment')}
          className="mb-4"
        >
          Retour
        </Button>
        <h1 className="text-3xl font-bold text-gray-900">Ajouter un Équipement</h1>
        <p className="text-gray-600 mt-1">Enregistrez un nouvel équipement de sécurité</p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Informations générales
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Type d'équipement"
              name="type"
              value={formData.type}
              onChange={handleChange}
              options={equipmentTypeOptions}
              placeholder="Sélectionner le type"
              error={errors.type}
              required
            />
            <Select
              label="Client"
              name="clientId"
              value={formData.clientId}
              onChange={handleChange}
              options={clientOptions}
              placeholder="Sélectionner un client"
              error={errors.clientId}
              required
            />
            <Select
              label="Site"
              name="siteId"
              value={formData.siteId}
              onChange={handleChange}
              options={siteOptions}
              placeholder={formData.clientId ? "Sélectionner un site" : "Choisir d'abord un client"}
              error={errors.siteId}
              disabled={!formData.clientId}
              required
            />
            <Input
              label="Numéro de série / Référence"
              name="serialNumber"
              value={formData.serialNumber}
              onChange={handleChange}
              error={errors.serialNumber}
              required
            />
          </div>
        </Card>

        <Card>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Dates importantes</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Date de vérification / Recharge"
              type="date"
              name="lastMaintenanceDate"
              value={formData.lastMaintenanceDate}
              onChange={handleChange}
              error={errors.lastMaintenanceDate}
              required
            />
            <Input
              label="Prochaine vérification"
              type="date"
              name="nextMaintenanceDate"
              value={formData.nextMaintenanceDate}
              onChange={handleChange}
              disabled
            />
            <Input
              label="Date de recharge"
              type="date"
              name="expirationDate"
              value={formData.expirationDate}
              onChange={handleChange}
            />
          </div>
        </Card>

        <Card>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Emplacement et État Physique
          </h2>
          <div className="space-y-4">
            <Input
              label="Emplacement"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="Ex: Couloir principal, Zone de stockage..."
            />
            <Textarea
              label="Aspect Physique / État de l'Équipement"
              name="physicalCondition"
              value={formData.physicalCondition}
              onChange={handleChange}
              rows={3}
            />
            <Textarea
              label="Notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={4}
            />
          </div>
        </Card>

        {errors.submit && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{errors.submit}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end space-x-4">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/equipment')}
          >
            Annuler
          </Button>
          <Button type="submit" variant="primary" loading={loading}>
            Enregistrer l'équipement
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddOtherEquipment;

